import { sha256 } from "@noble/hashes/sha2.js";

import { RPC_URLS } from "@/constants/chain-rpc";

import { CCTPTransientError } from "../errors";
import { getMessageTransmitterContract, SOLANA_CCTP_DOMAIN_ID } from "./contracts";

const SOLANA_NONCES_PER_ACCOUNT = 6400; // [u64; 100] = 100 * 64 bits

// -- Base58 codec (Bitcoin/Solana alphabet) ---------------------------------

const BASE58_ALPHABET = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";

export function base58Decode(str: string): Uint8Array {
  const bytes: number[] = [];
  for (const c of str) {
    const idx = BASE58_ALPHABET.indexOf(c);
    if (idx < 0) throw new Error(`Invalid base58 char: ${c}`);
    let carry = idx;
    for (let j = 0; j < bytes.length; j++) {
      const prev = bytes[j] ?? 0;
      carry += prev * 58;
      bytes[j] = carry & 0xff;
      carry >>= 8;
    }
    while (carry > 0) {
      bytes.push(carry & 0xff);
      carry >>= 8;
    }
  }
  for (const c of str) {
    if (c !== "1") break;
    bytes.push(0);
  }
  return Uint8Array.from(bytes.reverse());
}

export function base58Encode(bytes: Uint8Array): string {
  const digits: number[] = [0];
  for (const b of bytes) {
    let carry = b;
    for (let j = 0; j < digits.length; j++) {
      const prev = digits[j] ?? 0;
      carry += prev << 8;
      digits[j] = carry % 58;
      carry = Math.floor(carry / 58);
    }
    while (carry > 0) {
      digits.push(carry % 58);
      carry = Math.floor(carry / 58);
    }
  }
  let str = "";
  for (const b of bytes) {
    if (b !== 0) break;
    str += "1";
  }
  for (let i = digits.length - 1; i >= 0; i--) {
    const digit = digits[i];
    if (digit !== undefined) str += BASE58_ALPHABET[digit] ?? "";
  }
  return str;
}

// -- Ed25519 on-curve check (for Solana PDA derivation) ---------------------

const ZERO = BigInt(0);
const ONE = BigInt(1);
const TWO = BigInt(2);
const ED25519_P = TWO ** BigInt(255) - BigInt(19);

function modPow(base: bigint, exp: bigint, mod: bigint): bigint {
  let result = ONE;
  base = ((base % mod) + mod) % mod;
  while (exp > ZERO) {
    if (exp & ONE) result = (result * base) % mod;
    exp >>= ONE;
    base = (base * base) % mod;
  }
  return result;
}

const ED25519_D = (() => {
  const inv = modPow(BigInt(121666), ED25519_P - TWO, ED25519_P);
  return (((BigInt(-121665) * inv) % ED25519_P) + ED25519_P) % ED25519_P;
})();

/** Returns true if the 32-byte value is a valid ed25519 curve point. */
function isOnEd25519Curve(point: Uint8Array): boolean {
  if (point.length !== 32) return false;
  let y = ZERO;
  for (let i = 0; i < 32; i++) {
    const byte = point[i] ?? 0;
    y |= BigInt(byte) << BigInt(8 * i);
  }
  y &= (ONE << BigInt(255)) - ONE; // clear sign bit
  if (y >= ED25519_P) return false;

  const y2 = (y * y) % ED25519_P;
  const u = (y2 - ONE + ED25519_P) % ED25519_P;
  const v = (((ED25519_D * y2) % ED25519_P) + ONE) % ED25519_P;
  const vInv = modPow(v, ED25519_P - TWO, ED25519_P);
  const x2 = (u * vInv) % ED25519_P;
  if (x2 === ZERO) return true;

  const euler = modPow(x2, (ED25519_P - ONE) / TWO, ED25519_P);
  return euler === ONE;
}

// -- Solana PDA derivation --------------------------------------------------

const PDA_MARKER = new TextEncoder().encode("ProgramDerivedAddress");

export function findProgramAddress(seeds: Uint8Array[], programId: Uint8Array): Uint8Array {
  for (let bump = 255; bump >= 0; bump--) {
    const parts = [...seeds, Uint8Array.from([bump]), programId, PDA_MARKER];
    const totalLen = parts.reduce((sum, s) => sum + s.length, 0);
    const buffer = new Uint8Array(totalLen);
    let offset = 0;
    for (const s of parts) {
      buffer.set(s, offset);
      offset += s.length;
    }
    const hash = sha256(buffer);
    if (!isOnEd25519Curve(hash)) {
      return hash;
    }
  }
  throw new Error("Could not find valid PDA");
}

// -- Solana nonce check -----------------------------------------------------

/**
 * Check if a CCTP nonce has been consumed on Solana by reading the
 * MessageTransmitter's `UsedNonces` PDA account and inspecting the bitmap.
 *
 * Throws `CCTPTransientError` on RPC transport failure. A missing account
 * (PDA not yet initialized for this nonce bucket) is a legitimate "not
 * redeemed" and returns `false`.
 */
export async function checkNonceUsedOnSolana(
  sourceDomain: number,
  nonce: number,
): Promise<boolean> {
  const programId = getMessageTransmitterContract(SOLANA_CCTP_DOMAIN_ID);
  if (!programId) return false;

  // CCTP nonces are 1-indexed: first valid nonce is 1, not 0.
  // Bucket boundaries: [1..6400], [6401..12800], …
  // Formula matches the on-chain program and Wormhole SDK's calculateFirstNonce.
  const firstNonce =
    Math.floor((nonce - 1) / SOLANA_NONCES_PER_ACCOUNT) * SOLANA_NONCES_PER_ACCOUNT + 1;

  // PDA seeds: ["used_nonces", domain_string, delimiter, first_nonce_string]
  // Delimiter is "" for domains < 11, "-" for domains >= 11
  const enc = new TextEncoder();
  const delimiter = sourceDomain < 11 ? "" : "-";
  const seeds = [
    enc.encode("used_nonces"),
    enc.encode(sourceDomain.toString()),
    enc.encode(delimiter),
    enc.encode(firstNonce.toString()),
  ];

  const programIdBytes = base58Decode(programId);
  const pdaBytes = findProgramAddress(seeds, programIdBytes);
  const pdaAddress = base58Encode(pdaBytes);

  let response: Response;
  try {
    response = await fetch(RPC_URLS.Solana, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: 1,
        method: "getAccountInfo",
        params: [pdaAddress, { encoding: "base64" }],
      }),
    });
  } catch (err) {
    throw new CCTPTransientError(
      `Solana nonce RPC request failed (sourceDomain=${sourceDomain}, nonce=${nonce})`,
      err,
    );
  }

  if (!response.ok) {
    throw new CCTPTransientError(
      `Solana nonce RPC returned ${response.status} (sourceDomain=${sourceDomain}, nonce=${nonce})`,
    );
  }

  let data: { result?: { value?: { data?: [string, string] } } };
  try {
    data = (await response.json()) as typeof data;
  } catch (err) {
    throw new CCTPTransientError(
      `Solana nonce RPC returned invalid JSON (sourceDomain=${sourceDomain}, nonce=${nonce})`,
      err,
    );
  }

  const encoded = data.result?.value?.data?.[0];
  if (!encoded) return false;

  const raw = Uint8Array.from(atob(encoded), (c) => c.charCodeAt(0));

  // Account layout: 8 (discriminator) + 4 (remote_domain) + 8 (first_nonce) + 800 (used_nonces)
  const HEADER_SIZE = 20;
  if (raw.length < HEADER_SIZE) return false;

  const nonceIndex = nonce - firstNonce;
  const wordIndex = Math.floor(nonceIndex / 64);
  const bitIndex = nonceIndex % 64;
  const wordOffset = HEADER_SIZE + wordIndex * 8;
  if (wordOffset + 8 > raw.length) return false;

  const view = new DataView(raw.buffer, raw.byteOffset, raw.byteLength);
  const low = view.getUint32(wordOffset, true);
  const high = view.getUint32(wordOffset + 4, true);
  const word = BigInt(low) | (BigInt(high) << BigInt(32));

  return (word & (ONE << BigInt(bitIndex))) !== ZERO;
}
